<template>
	<div class="super-servers-container">
		<AppCard :title="getString('servers')">
			<template #body>
				<div class="iframe-container">
					<iframe 
						:src="dashboardUrl"
						title="CloudWatch Dashboard"
						frameborder="0"
						allowfullscreen>
					</iframe>
				</div>
			</template>
		</AppCard>
		
		<AppCard :title="getString('increase_resources')" class="mt-20">
			<template #body>
				<AppList :items="configurationItems" />
			</template>
		</AppCard>
		
		<AppNotification 
			ref="success-scaling" 
			:title="getString('success')" 
			:body-text="getString('server_resources_updated')" 
			:type="1" />
		
		<AppNotification 
			ref="error-scaling" 
			:title="getString('error')" 
			:body-text="errorMessage || getString('server_resources_update_failed')" 
			:type="-2" />
	</div>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppList from "@/shared/components/AppList.vue";
import AppNotification from "@/shared/components/AppNotification.vue";
import API from "@/shared/helpers/API";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useSuperStore, useUserStore} from "@/user/stores";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const notificationStore = useNotificationStore();
		const userStore = useUserStore();
		const superStore = useSuperStore();
		return {getString: languageStore.getString, notificationStore, userStore, superStore, TEST};
	},
	name: "SuperServers",
	components: {AppCard, AppList, AppNotification},
	data() {
		return {
			dashboardUrl: TEST ?
				"https://cloudwatch.amazonaws.com/dashboard.html?dashboard=Qromo-AWS-TEST&context=eyJSIjoidXMtZWFzdC0xIiwiRCI6ImN3LWRiLTQyNzcwMTcwMDE2NyIsIlUiOiJ1cy1lYXN0LTFfV2FMb29HOHlmIiwiQyI6IjY0dWlkczJrMWphNDBpcWllNWZvNzRmajQ4IiwiSSI6InVzLWVhc3QtMTplMTUyNjJhYi0zYzUxLTQ1YmEtODIyYS1lNmU5Y2U2YTdhN2UiLCJNIjoiUHVibGljIn0=&start=PT7D&end=null" :
				"https://cloudwatch.amazonaws.com/dashboard.html?dashboard=Qromo-AWS&context=eyJSIjoidXMtZWFzdC0xIiwiRCI6ImN3LWRiLTQyNzcwMTcwMDE2NyIsIlUiOiJ1cy1lYXN0LTFfV2FMb29HOHlmIiwiQyI6IjY0dWlkczJrMWphNDBpcWllNWZvNzRmajQ4IiwiSSI6InVzLWVhc3QtMTphNjUzM2Q2OS00NWI5LTQxMDEtYTFiNC01MjI3YWY5ZjdiZWUiLCJNIjoiUHVibGljIn0=&start=PT7D&end=null",
			serverNumbers: 25,
			dbSize: 12,
			hoursOfTraffic: 1,
			saving: false,
			errorMessage: null
		};
	},
	computed: {
		configurationItems() {
			return [
				{
					title: this.getString('server_numbers'),
					numberInput: true,
					min: 25,
					max: 100,
					value: this.serverNumbers,
					callbackNumber: (v) => this.serverNumbers = v
				},
				{
					title: this.getString('db_size'),
					numberInput: true,
					min: 12,
					max: 32,
					value: this.dbSize,
					callbackNumber: (v) => this.dbSize = v
				},
				{
					title: this.getString('hours_of_traffic'),
					numberInput: true,
					min: 1,
					max: 24,
					value: this.hoursOfTraffic,
					callbackNumber: (v) => this.hoursOfTraffic = v
				},
				{
					title: this.getString('minimum_estimated_cost'),
					rightText: `$${this.estimatedCost.toFixed(2)}`,
					rightPrimaryText: true
				},
				{
					title: '',
					buttonTitle: this.getString('confirm_changes'),
					buttonLoading: this.saving,
					callbackButton: this.confirmChanges
				}
			];
		},
		estimatedCost() {
			const acuCost = this.hoursOfTraffic * this.dbSize * 0.14 * 1.22;
			const ecsCost = this.hoursOfTraffic * this.serverNumbers * 0.05 * 1.22;
			return acuCost + ecsCost;
		}
	},
	methods: {
		confirmChanges() {
			this.saving = true;
			this.errorMessage = null;
			
			API.init().superUpdateServerResources(
				this.serverNumbers,
				this.dbSize,
				this.hoursOfTraffic,
				(response) => {
					this.saving = false;
					if (response.result === 'OK') {
						this.$refs['success-scaling'].openNotification();
					} else {
						// Build detailed error message if available
						if (response.ecs_result && !response.ecs_result.success) {
							this.errorMessage = `ECS: ${response.ecs_result.error}`;
						} else if (response.aurora_result && !response.aurora_result.success) {
							this.errorMessage = `Aurora: ${response.aurora_result.error}`;
						} else if (response.details) {
							this.errorMessage = this.getString(response.details) || response.details;
						}
						this.$refs['error-scaling'].openNotification();
					}
				}
			);
		}
	}
};
</script>

<style lang="scss" scoped>
.super-servers-container {
	width: 100%;
	
	:deep(.card-body) {
		padding: 0;
	}
	
	.iframe-container {
		width: 100%;
		min-height: 400px;
		height: calc(100vh - 200px);

		iframe {
			width: 100%;
			min-height: 400px;
			height: calc(100vh - 200px);
			border: none;
			border-bottom-left-radius: 13px;
			border-bottom-right-radius: 13px;
		}
	}
	
	.mt-20 {
		margin-top: 20px;
	}
}
</style>